// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  PALHELPPAGE.JSX - VERSION: 2025.12.29.0215                                ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.12.29.0215
// ============================================================================
// CHANGELOG:
// 2025.12.26.1700 - Complete rebuild with optimized UX flow:
//                   Question teaser → "I Think I Know!" → Name entry → 
//                   Answer → Spin to Win signup
// 2025.12.24.1331 - Initial version
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = 'https://api.traktag.com/api';

const PalHelpPage = ({ isLoggedIn, playerData, onLoginRequired }) => {
  const { code } = useParams();
  const navigate = useNavigate();
  
  // Flow states
  const [currentStep, setCurrentStep] = useState('loading'); // loading, teaser, name, question, result, spin, complete
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [palRequest, setPalRequest] = useState(null);
  const [helperName, setHelperName] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  // Inline signup states
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  
  // Spin wheel states
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinResult, setSpinResult] = useState(null);
  const [spinRotation, setSpinRotation] = useState(0);
  
  // Fetch pal request on mount
  useEffect(() => {
    fetchPalRequest();
  }, [code]);
  
  const fetchPalRequest = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/pal/${code}`);
      const data = await response.json();
      
      // Handle expired links - show signup prompt instead of error
      if (response.status === 410 || data.expired) {
        setCurrentStep('expired');
        return;
      }
      
      if (!response.ok) {
        setError(data.error || 'Failed to load help request');
        setCurrentStep('error');
        return;
      }
      
      setPalRequest(data);
      
      // Always show teaser - allow multiple people to answer
      // The teaser will show a note if someone already answered
      setCurrentStep('teaser');
      
    } catch (err) {
      setError('Failed to connect to server');
      setCurrentStep('error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleIThinkIKnow = () => {
    setCurrentStep('name');
  };
  
  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (helperName.trim().length >= 1) {
      setCurrentStep('question');
    }
  };
  
  const handleSelectAnswer = (answer) => {
    setSelectedAnswer(answer);
  };
  
  const handleSubmitAnswer = async () => {
    if (!selectedAnswer || !helperName.trim()) return;
    
    setSubmitting(true);
    
    try {
      // Submit answer (works for both logged in and anonymous)
      const token = localStorage.getItem('trak_token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/pal/${code}/answer`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          selected_answer: selectedAnswer,
          helper_name: helperName.trim()
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Failed to submit answer');
        return;
      }
      
      setResult(data);
      
      // If already logged in, show complete screen
      // If not logged in, show spin to win
      if (isLoggedIn) {
        setCurrentStep('complete');
      } else {
        setCurrentStep('result');
      }
      
    } catch (err) {
      setError('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSpinToWin = () => {
    setCurrentStep('spin');
    setIsSpinning(true);
    
    // Random spin between 1800-2520 degrees (5-7 full rotations)
    const baseRotation = 1800 + Math.random() * 720;
    // Add extra to land on 50-100 segment
    const bonusAmount = 50 + Math.floor(Math.random() * 51); // 50-100
    setSpinResult(bonusAmount);
    setSpinRotation(baseRotation);
    
    // Stop spinning after 4 seconds
    setTimeout(() => {
      setIsSpinning(false);
    }, 4000);
  };
  
  // Inline signup handler
  const handleInlineSignup = async () => {
    if (!signupEmail || !signupPassword) {
      setSignupError('Please enter email and password');
      return;
    }
    if (signupPassword.length < 6) {
      setSignupError('Password must be at least 6 characters');
      return;
    }
    
    setSignupLoading(true);
    setSignupError('');
    
    try {
      // Register the user
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: helperName,
          email: signupEmail,
          password: signupPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setSignupError(data.error || 'Registration failed');
        setSignupLoading(false);
        return;
      }
      
      // Store token
      localStorage.setItem('trak_token', data.token);
      
      // Claim referral bonus
      try {
        const bonusResponse = await fetch(`${API_URL}/pal/claim-referral`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.token}`
          },
          body: JSON.stringify({
            code: code,
            bonus_amount: 75 // Give them 75 TRAKs for helping
          })
        });
        
        const bonusData = await bonusResponse.json();
        console.log('✅ Referral bonus claimed:', bonusData);
      } catch (err) {
        console.error('Bonus claim error:', err);
      }
      
      // Redirect to game
      setCurrentStep('signup_complete');
      
    } catch (err) {
      setSignupError('Connection failed. Please try again.');
    }
    
    setSignupLoading(false);
  };
  
  const handleSignUp = () => {
    // Store referral info
    localStorage.setItem('palReferralCode', code);
    localStorage.setItem('palBonusAmount', spinResult || 50);
    localStorage.setItem('palHelperName', helperName);
    onLoginRequired('register');
  };
  
  const handleStartPlaying = () => {
    navigate('/');
  };
  
  // ============================================
  // RENDER: Loading
  // ============================================
  if (currentStep === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">🤔</div>
          <p className="text-white text-lg">Loading question...</p>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Error
  // ============================================
  if (currentStep === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full text-center border border-gray-700">
          <div className="text-5xl mb-4">😕</div>
          <h1 className="text-white text-xl font-bold mb-2">Oops!</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-500 to-teal-500 text-white font-bold py-3 px-6 rounded-xl"
          >
            Go to TRAKtag
          </button>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Expired Link (30+ days old)
  // ============================================
  if (currentStep === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <h1 className="text-2xl font-black">
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700 text-center">
            <div className="text-6xl mb-4">⏰</div>
            <h1 className="text-white text-2xl font-bold mb-2">Question Closed</h1>
            <p className="text-gray-400 mb-6">
              Sorry! This help request has expired.<br/>
              The player has already moved on.
            </p>
            
            {/* But still encourage signup! */}
            <div className="bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border-2 border-teal-500 rounded-xl p-4 mb-4">
              <div className="text-4xl mb-2">🎮</div>
              <p className="text-teal-400 text-lg font-bold mb-2">Want to play TRAKtag?</p>
              <p className="text-teal-300 text-sm mb-3">
                Join thousands of players answering quiz questions and earning TRAKs!
              </p>
              
              <button
                onClick={() => onLoginRequired('register')}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-white font-black py-4 px-6 rounded-xl text-lg shadow-lg transition-all"
              >
                🎁 Sign Up & Get 50 Free TRAKs!
              </button>
            </div>
            
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 text-sm hover:text-gray-400"
            >
              Just browsing
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Already Answered - Show answer + signup form
  // ============================================
  if (currentStep === 'already_answered') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <img src="/traktag-logo.png" alt="TRAKtag" className="h-10 mx-auto" onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}/>
          <h1 className="text-2xl font-black" style={{display: 'none'}}>
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-green-500 text-center">
            <div className="text-5xl mb-3">🙏</div>
            <h1 className="text-white text-2xl font-bold mb-2">Thanks for Helping!</h1>
            <p className="text-gray-300 mb-4">
              <span className="text-teal-400 font-bold">{palRequest?.player_name}</span> already got help with this question
            </p>
            
            {/* Show the answer that was given */}
            {palRequest?.answered_by && (
              <div className="bg-teal-800/30 border border-teal-500 rounded-xl p-3 mb-5">
                <p className="text-teal-300 text-sm">{palRequest.answered_by} answered:</p>
                <p className="text-white font-bold text-lg">"{palRequest.selected_answer || 'Answer submitted'}"</p>
              </div>
            )}
            
            {/* Inline Signup Form */}
            <div className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border-2 border-yellow-500 rounded-xl p-4 mb-4">
              <div className="text-3xl mb-2">🎁</div>
              <p className="text-yellow-400 text-lg font-bold mb-1">Want to Play Too?</p>
              <p className="text-yellow-300 text-sm mb-4">
                Sign up and get <span className="font-bold">50 Free TRAKs!</span>
              </p>
              
              {/* Pre-filled signup form */}
              <div className="space-y-3 text-left">
                <div>
                  <label className="text-gray-400 text-xs">Your Name</label>
                  <input
                    type="text"
                    value={helperName}
                    onChange={(e) => setHelperName(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Email</label>
                  <input
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Password</label>
                  <input
                    type="password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="Create a password"
                  />
                </div>
                
                {signupError && (
                  <p className="text-red-400 text-xs">{signupError}</p>
                )}
                
                <button
                  onClick={handleInlineSignup}
                  disabled={signupLoading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-black py-3 px-6 rounded-xl text-lg shadow-lg transition-all"
                >
                  {signupLoading ? '⏳ Creating...' : '🎮 Sign Up & Play!'}
                </button>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 text-sm hover:text-gray-400"
            >
              No thanks, I'll pass this time
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Step 1 - Question Teaser + "I Think I Know!"
  // ============================================
  if (currentStep === 'teaser') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <img src="/traktag-logo.png" alt="TRAKtag" className="h-10 mx-auto" onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}/>
          <h1 className="text-2xl font-black" style={{display: 'none'}}>
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700">
            {/* Help request header */}
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">🆘</div>
              <h2 className="text-white text-xl font-bold mb-1">
                <span className="text-teal-400">{palRequest.player_name}</span> needs your help!
              </h2>
              <p className="text-gray-400 text-sm">⏰ Reply quickly - they're waiting!</p>
              
              {/* Note if someone already answered */}
              {palRequest.already_answered && (
                <p className="text-yellow-400 text-xs mt-2">
                  💡 {palRequest.answered_by} already answered - but you can still help!
                </p>
              )}
            </div>
            
            {/* Question Preview */}
            <div className="bg-blue-900/50 border border-blue-500 rounded-xl p-4 mb-6">
              <p className="text-blue-300 text-xs mb-2 text-center">THE QUESTION:</p>
              <p className="text-white text-lg font-medium text-center">
                "{palRequest.question_text}"
              </p>
            </div>
            
            {/* Answers blurred/hidden */}
            <div className="space-y-2 mb-6">
              {palRequest.answers.slice(0, 4).map((_, index) => (
                <div
                  key={index}
                  className="w-full p-3 rounded-xl bg-gray-700/50 text-gray-500 blur-sm select-none"
                >
                  <span className="mr-2">{['A', 'B', 'C', 'D'][index]}.</span>
                  ████████████
                </div>
              ))}
            </div>
            
            {/* I Think I Know button */}
            <button
              onClick={handleIThinkIKnow}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-black py-4 px-6 rounded-xl text-lg shadow-lg transition-all animate-pulse"
            >
              🙋 I Think I Know This!
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full text-gray-500 text-sm mt-3 hover:text-gray-400"
            >
              🤷 Not sure, skip this
            </button>
            
            {/* Bonus hint */}
            <div className="mt-4 text-center">
              <p className="text-yellow-400 text-sm font-medium">
                🎁 Help & earn <span className="font-bold">50-100 TRAKs</span> reward!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Step 2 - Name Entry
  // ============================================
  if (currentStep === 'name') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <h1 className="text-2xl font-black">
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Step 1 of 3</span>
                <span>Your Name</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
              </div>
            </div>
            
            {/* Icon and text */}
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">👋</div>
              <h2 className="text-white text-xl font-bold mb-2">What's your first name?</h2>
              <p className="text-gray-400 text-sm">
                So <span className="text-teal-400 font-bold">{palRequest.player_name}</span> knows who helped! 😊
              </p>
            </div>
            
            {/* Name input */}
            <form onSubmit={handleNameSubmit}>
              <input
                type="text"
                value={helperName}
                onChange={(e) => setHelperName(e.target.value)}
                placeholder="Enter your first name"
                className="w-full bg-gray-700 border border-gray-600 text-white text-lg px-4 py-4 rounded-xl mb-4 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/50 text-center"
                autoFocus
                maxLength={30}
              />
              
              <button
                type="submit"
                disabled={helperName.trim().length < 1}
                className={`w-full font-bold py-4 px-6 rounded-xl text-lg transition-all ${
                  helperName.trim().length >= 1
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue to Question →
              </button>
            </form>
            
            <button
              onClick={() => setCurrentStep('teaser')}
              className="w-full text-gray-500 text-sm mt-3 hover:text-gray-400"
            >
              ← Go back
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Step 3 - Answer Question
  // ============================================
  if (currentStep === 'question') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <h1 className="text-2xl font-black">
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700">
            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Step 2 of 3</span>
                <span>Pick Answer</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full w-2/3 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
              </div>
            </div>
            
            {/* Helper greeting */}
            <div className="text-center mb-4">
              <p className="text-gray-400 text-sm">
                Thanks <span className="text-teal-400 font-bold">{helperName}</span>! Now pick the answer:
              </p>
            </div>
            
            {/* Question */}
            <div className="bg-blue-900/50 border border-blue-500 rounded-xl p-4 mb-4">
              <p className="text-white text-lg font-medium text-center">
                {palRequest.question_text}
              </p>
            </div>
            
            {/* Answers */}
            <div className="space-y-2 mb-4">
              {palRequest.answers.map((answer, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectAnswer(answer)}
                  className={`w-full p-3 rounded-xl text-left font-medium transition-all ${
                    selectedAnswer === answer
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white ring-2 ring-yellow-400 shadow-lg'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  <span className="mr-3 font-bold">{['A', 'B', 'C', 'D'][index]}.</span>
                  {answer}
                </button>
              ))}
              
              {/* Don't Know option */}
              <button
                onClick={() => handleSelectAnswer("I don't know")}
                className={`w-full p-3 rounded-xl text-left font-medium transition-all ${
                  selectedAnswer === "I don't know"
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white ring-2 ring-yellow-400 shadow-lg'
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500 border border-dashed border-gray-500'
                }`}
              >
                <span className="mr-3">🤷</span>
                I don't know - but I'll sign up anyway!
              </button>
            </div>
            
            {/* Submit button */}
            <button
              onClick={handleSubmitAnswer}
              disabled={!selectedAnswer || submitting}
              className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all ${
                selectedAnswer && !submitting
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white shadow-lg'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                '⏳ Sending...'
              ) : selectedAnswer ? (
                `📤 Send Answer to ${palRequest.player_name}`
              ) : (
                'Select an answer'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Step 4 - Thank You + Inline Signup
  // ============================================
  if (currentStep === 'result') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <img src="/traktag-logo.png" alt="TRAKtag" className="h-10 mx-auto" onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}/>
          <h1 className="text-2xl font-black" style={{display: 'none'}}>
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-green-500 text-center">
            {/* Big Thank You */}
            <div className="text-6xl mb-3">🙏</div>
            <h2 className="text-white text-2xl font-bold mb-2">Thank You, {helperName}!</h2>
            
            <p className="text-gray-300 mb-4">
              Your answer has been sent to <span className="text-teal-400 font-bold">{palRequest.player_name}</span>
            </p>
            
            {/* Answer confirmation */}
            <div className="bg-teal-800/30 border border-teal-500 rounded-xl p-3 mb-5">
              <p className="text-teal-300 text-sm">You answered:</p>
              <p className="text-white font-bold text-lg">"{selectedAnswer}"</p>
            </div>
            
            {/* Inline Signup Form */}
            <div className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border-2 border-yellow-500 rounded-xl p-4">
              <div className="text-3xl mb-2">🎁</div>
              <p className="text-yellow-400 text-lg font-bold mb-1">Claim Your Reward!</p>
              <p className="text-yellow-300 text-sm mb-4">
                Sign up now to get <span className="font-bold">50-100 TRAKs</span> for helping!
              </p>
              
              {/* Pre-filled signup form */}
              <div className="space-y-3 text-left">
                <div>
                  <label className="text-gray-400 text-xs">Your Name</label>
                  <input
                    type="text"
                    value={helperName}
                    onChange={(e) => setHelperName(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Email</label>
                  <input
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Password</label>
                  <input
                    type="password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="Create a password"
                  />
                </div>
                
                {signupError && (
                  <p className="text-red-400 text-xs">{signupError}</p>
                )}
                
                <button
                  onClick={handleInlineSignup}
                  disabled={signupLoading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-black py-3 px-6 rounded-xl text-lg shadow-lg transition-all"
                >
                  {signupLoading ? '⏳ Creating...' : '🎰 Sign Up & Claim TRAKs!'}
                </button>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 text-sm hover:text-gray-400 mt-4"
            >
              No thanks, I'll pass this time
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Signup Complete - Welcome!
  // ============================================
  if (currentStep === 'signup_complete') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <img src="/traktag-logo.png" alt="TRAKtag" className="h-10 mx-auto" onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}/>
          <h1 className="text-2xl font-black" style={{display: 'none'}}>
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-green-500 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-white text-3xl font-black mb-2">Welcome, {helperName}!</h2>
            
            <div className="bg-green-800/30 border border-green-500 rounded-xl p-4 mb-6">
              <p className="text-green-300 text-lg">You've earned</p>
              <p className="text-yellow-400 text-4xl font-black">+75 TRAKs!</p>
              <p className="text-green-300 text-sm mt-2">for helping your friend</p>
            </div>
            
            <p className="text-gray-400 mb-4">
              Your account is ready! Start playing quizzes and earn more TRAKs.
            </p>
            
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-white font-black py-4 px-6 rounded-xl text-xl shadow-lg transition-all"
            >
              🎮 Start Playing!
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Step 5 - Spin Wheel
  // ============================================
  if (currentStep === 'spin') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <h1 className="text-2xl font-black">
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700 text-center">
            <h2 className="text-yellow-400 text-2xl font-black mb-4">
              {isSpinning ? '🎰 SPINNING...' : '🎉 YOU WON!'}
            </h2>
            
            {/* Spin Wheel Visual */}
            <div className="relative w-48 h-48 mx-auto mb-6">
              {/* Wheel */}
              <div 
                className={`w-full h-full rounded-full border-4 border-yellow-500 overflow-hidden transition-transform ${isSpinning ? 'duration-[4000ms]' : 'duration-0'}`}
                style={{ 
                  transform: `rotate(${spinRotation}deg)`,
                  background: 'conic-gradient(from 0deg, #10b981 0deg 60deg, #14b8a6 60deg 120deg, #06b6d4 120deg 180deg, #3b82f6 180deg 240deg, #8b5cf6 240deg 300deg, #ec4899 300deg 360deg)'
                }}
              >
                {/* Segments */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white font-black text-xs transform -rotate-30">50</div>
                </div>
              </div>
              
              {/* Center */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gray-900 rounded-full border-4 border-yellow-400 flex items-center justify-center">
                <span className="text-yellow-400 font-black text-lg">
                  {isSpinning ? '?' : spinResult}
                </span>
              </div>
              
              {/* Pointer */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-yellow-500"></div>
              </div>
            </div>
            
            {/* Result */}
            {!isSpinning && (
              <>
                <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-2 border-yellow-500 rounded-xl p-4 mb-4">
                  <p className="text-yellow-300 text-sm">You won:</p>
                  <p className="text-yellow-400 text-5xl font-black">+{spinResult}</p>
                  <p className="text-yellow-300 text-lg font-bold">TRAKs!</p>
                </div>
                
                <button
                  onClick={handleSignUp}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-black py-4 px-6 rounded-xl text-lg shadow-lg transition-all mb-3"
                >
                  ✨ Sign Up & Claim {spinResult} TRAKs!
                </button>
                
                <p className="text-gray-400 text-sm mb-2">Already have an account?</p>
                <button
                  onClick={() => onLoginRequired('login')}
                  className="text-teal-400 hover:text-teal-300 font-medium"
                >
                  Login to claim
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Step 6 - Complete (for logged in users - they CAN see right/wrong)
  // ============================================
  if (currentStep === 'complete') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-900 via-gray-900 to-gray-800 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4 text-center">
          <h1 className="text-2xl font-black">
            <span className="text-yellow-400">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700 text-center">
            {/* Celebration */}
            <div className="text-6xl mb-4">🎉</div>
            
            <h1 className="text-yellow-400 text-2xl font-black mb-2">THANKS FOR HELPING!</h1>
            
            {/* Certificate */}
            <div className="bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl p-1 mb-4 shadow-2xl">
              <div className="bg-gradient-to-br from-yellow-300 to-amber-400 rounded-lg p-4">
                <p className="text-yellow-800 text-sm font-medium">HELPER BONUS</p>
                <div className="text-4xl my-2">🏆</div>
                <p className="text-yellow-900 text-4xl font-black">+{result?.bonus_traks || 50}</p>
                <p className="text-yellow-800 text-lg font-bold">TRAKs</p>
              </div>
            </div>
            
            {/* Thank you message */}
            <p className="text-white text-lg mb-2">
              You helped <span className="text-teal-400 font-bold">{palRequest.player_name}</span>!
            </p>
            
            {/* Answer feedback - ONLY for logged in users */}
            <div className={`rounded-xl p-3 mb-4 ${result?.is_correct ? 'bg-green-600/30 border border-green-500' : 'bg-orange-600/30 border border-orange-500'}`}>
              <p className="text-gray-300 text-sm mb-1">Your answer: "{selectedAnswer}"</p>
              {result?.is_correct ? (
                <p className="text-green-400 font-bold">✅ That was CORRECT!</p>
              ) : (
                <p className="text-orange-400">
                  The correct answer was: <span className="font-bold">{result?.correct_answer}</span>
                </p>
              )}
            </div>
            
            {/* CTA */}
            <button
              onClick={handleStartPlaying}
              className="w-full bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-400 hover:to-teal-400 text-white font-black py-4 px-6 rounded-xl text-lg shadow-lg transition-all"
            >
              🎮 Continue Playing!
            </button>
            
            <p className="text-gray-500 text-xs mt-4">
              TRAKs have been added to your account
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Fallback
  return null;
};

export default PalHelpPage;
